import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDashboardStats } from '../services/adminAnalyticsService';
import TimeRangeSelector from '../components/TimeRangeSelector';
import { can, canAccess } from '../utils/adminPermissions';
import { getStatusLabel } from '../utils/statusLabels';
import useAutoRefresh from '../hooks/useAutoRefresh';
import {
  USERS_TABLE_ID, STORE_TABLE_ID, PRODUCTS_TABLE_ID,
  ORDERS_TABLE_ID, DRIVERS_TABLE_ID, REQUESTS_TABLE_ID,
} from '../lib/appwrite';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import './DashboardPage.css';

/* ── Palette (brand green scale) ────────────────────────────────── */
const G = {
  900: '#054F31',
  800: '#05603A',
  700: '#027A48',
  600: '#039855',
  500: '#12B76A',
  400: '#32D583',
  300: '#6CE9A6',
  200: '#A6F4C5',
  100: '#D1FADF',
  50:  '#ECFDF3',
};
const MUTED  = '#D0D5DD';
const AMBER  = '#F79009';
const RED    = '#F04438';
const AXIS   = { fontSize: 11, fill: '#98A2B3' };

const USER_COLORS = [G[600], G[400], G[200], G[800]];

const STATUS_COLORS = {
  pending:    AMBER,
  confirmed:  G[400],
  processing: G[300],
  shipped:    G[500],
  delivered:  G[600],
  completed:  G[700],
  cancelled:  RED,
  canceled:   RED,
};

/* ── Formatters ─────────────────────────────────────────────────── */
const fmt = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '0' : n.toLocaleString('en-TZ');
};

const fmtK = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-TZ');
};

/** Round up to a "nice" axis maximum (e.g. 1.5, 2.5, 6 × 10^k). */
const niceCeil = (n) => {
  if (!n || n <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(n));
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => s * mag >= n);
  return step * mag;
};

const growthOf = (last, prev) => (prev > 0 ? ((last - prev) / prev) * 100 : 0);

/* ── Small building blocks ──────────────────────────────────────── */
const Growth = ({ value, hint }) => {
  if (value == null || !isFinite(value) || value === 0) {
    return <div className="dash-growth dash-growth-flat">{hint ?? '—'}</div>;
  }
  const up = value > 0;
  return (
    <div className={`dash-growth ${up ? 'dash-growth-up' : 'dash-growth-down'}`} title={hint}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        {up
          ? <><path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/></>
          : <><path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/></>}
      </svg>
      <span>{up ? '+' : '−'}{Math.abs(value).toFixed(1)}%</span>
    </div>
  );
};

const MiniBars = ({ data, dataKey, color = G[600] }) => {
  if (!data?.length || data.every((d) => !d[dataKey])) return null;
  return (
    <div className="dash-kpi-bars">
      <ResponsiveContainer width="100%" height={44}>
        <BarChart data={data} barCategoryGap="22%" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <Bar dataKey={dataKey} fill={color} radius={[2, 2, 0, 0]} isAnimationActive={false} minPointSize={2} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const KpiCard = ({ label, value, unit, growth, hint, visual }) => (
  <div className="dash-kpi">
    <div className="dash-kpi-label">{label}</div>
    <Growth value={growth} hint={hint} />
    <div className="dash-kpi-bottom">
      <div className="dash-kpi-value">
        {value}
        {unit && <span className="dash-kpi-unit">{unit}</span>}
      </div>
      {visual}
    </div>
  </div>
);

const Card = ({ title, children, onAction, actionLabel, right, className = '' }) => (
  <div className={`dash-card ${className}`}>
    <div className="dash-card-head">
      <h3 className="dash-card-title">{title}</h3>
      {right}
      {onAction && (
        <button type="button" className="dash-card-action" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
    {children}
  </div>
);

const Donut = ({ data, total, totalLabel, height = 180, inner = 58, outer = 80 }) => (
  <div className="dash-donut-wrap">
    <div className="dash-donut-chart">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={inner} outerRadius={outer}
            paddingAngle={2} cornerRadius={4} dataKey="value" startAngle={90} endAngle={-270} stroke="none">
            {data.map((d) => <Cell key={d.name} fill={d.color} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [v, n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="dash-donut-center">
        <div className="dash-donut-num">{total}</div>
        <div className="dash-donut-lbl">{totalLabel}</div>
      </div>
    </div>
    <div className="dash-legend-list">
      {data.map((d) => (
        <div key={d.name} className="dash-legend-row">
          <span className="dash-legend-dot" style={{ background: d.color }} />
          <span className="dash-legend-name">{d.name}</span>
          <span className="dash-legend-val">{d.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="dash-tooltip">
      {label && <div className="dash-tooltip-label">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey} className="dash-tooltip-row">
          <span className="dash-tooltip-dot" style={{ background: p.color }} />
          <span>{p.name}</span>
          <span className="dash-tooltip-val">{fmt(p.value)}{suffix}</span>
        </div>
      ))}
    </div>
  );
};

/* Tooltip for the diverging chart: orders are stored negative for plotting. */
const TrendTooltip = ({ active, payload, label, revenueLabel, ordersLabel, revColor, ordColor }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-label">{label}</div>
      <div className="dash-tooltip-row">
        <span className="dash-tooltip-dot" style={{ background: revColor }} />
        <span>{revenueLabel}</span>
        <span className="dash-tooltip-val">{fmt(row.revenue)} TZS</span>
      </div>
      <div className="dash-tooltip-row">
        <span className="dash-tooltip-dot" style={{ background: ordColor }} />
        <span>{ordersLabel}</span>
        <span className="dash-tooltip-val">{fmt(row.orders)}</span>
      </div>
    </div>
  );
};

const insightIcon = (id, up) => {
  const svg = (children) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
  switch (id) {
    case 'revenue':
    case 'orders':
      return up
        ? svg(<><path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/></>)
        : svg(<><path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/></>);
    case 'deliveries':
      return svg(<><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62L18.3 9.38a1 1 0 0 0-.78-.38H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></>);
    default: { // lowStock / outOfStock: a box with a minus or a cross
      const box = <><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/><path d="m7.5 4.27 9 5.15"/><path d="M3.29 7 12 12l8.71-5"/><path d="M12 22V12"/></>;
      return id === 'outOfStock'
        ? svg(<>{box}<path d="m17 13 5 5m-5 0 5-5"/></>)
        : svg(<>{box}<path d="M16 16h6"/></>);
    }
  }
};

const SummaryStat = ({ icon, value, label }) => (
  <div className="dash-summary-stat">
    <div className="dash-summary-icon">{icon}</div>
    <div className="dash-summary-val">{value}</div>
    <div className="dash-summary-lbl">{label}</div>
  </div>
);

/* ── Page ───────────────────────────────────────────────────────── */
const DashboardPage = () => {
  const { t } = useTranslation();
  const { adminRole }     = useOutletContext() || {};
  const location          = useLocation();
  const permissionMessage = location.state?.message;
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [focus, setFocus] = useState('revenue');
  const navigate = useNavigate();

  const canSeeFullDashboard = adminRole && can(adminRole, 'full', 'dashboard');
  const canAccessProducts   = adminRole && canAccess(adminRole, 'products');
  const canAccessRequests   = adminRole && canAccess(adminRole, 'requests');
  const canAccessAnalytics  = adminRole && canAccess(adminRole, 'analytics');

  const loadStats = useCallback(() => {
    setLoading(true);
    getDashboardStats({ timeRange })
      .then(setStats)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [timeRange]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useAutoRefresh(loadStats, {
    realtimeTables: [
      USERS_TABLE_ID, STORE_TABLE_ID, PRODUCTS_TABLE_ID,
      ORDERS_TABLE_ID, DRIVERS_TABLE_ID, REQUESTS_TABLE_ID,
    ],
  });

  if (loading && !stats) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" />
        <p>Loading dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-page">
        <h1 className="dash-title">Dashboard</h1>
        <div className="dash-error">{error}</div>
      </div>
    );
  }

  /* ── Derived data ───────────────────────────────────────────── */
  const trend      = stats?.orderTrend ?? [];
  const lowStock   = stats?.lowStockCount   ?? 0;
  const outOfStock = stats?.outOfStockCount ?? 0;
  const delPending = stats?.deliveryPending ?? 0;
  const hasTrend   = trend.some((d) => d.orders > 0 || d.revenue > 0);

  // Mini bars show the most recent buckets only
  const recent = trend.slice(-12).map((d) => ({
    ...d,
    aov: d.orders > 0 ? d.revenue / d.orders : 0,
  }));
  const aovGrowth = recent.length > 1
    ? growthOf(recent[recent.length - 1].aov, recent[recent.length - 2].aov)
    : 0;

  // Diverging chart: revenue above zero (right axis), orders below zero (left axis)
  const divergingData = trend.map((d) => ({ ...d, ordersNeg: -d.orders }));
  const revMax = niceCeil(Math.max(...trend.map((d) => d.revenue), 0));
  const ordMax = niceCeil(Math.max(...trend.map((d) => d.orders), 0));
  const barSize = Math.max(5, Math.min(14, Math.floor(420 / Math.max(trend.length, 1))));
  const revTicks = [0, revMax / 2, revMax];
  const ordTicks = [-ordMax, -ordMax / 2].filter(Number.isInteger);

  const userPieData = [
    { name: t('dashboard.sellers'), value: stats?.sellers ?? 0 },
    { name: t('dashboard.farmers'), value: stats?.buyers  ?? 0 },
    { name: t('nav.drivers'),       value: stats?.drivers ?? 0 },
    { name: t('dashboard.admins'),  value: stats?.admins  ?? 0 },
  ]
    .map((d, i) => ({ ...d, color: USER_COLORS[i] }))
    .filter((d) => d.value > 0);

  const topProductsData = (stats?.topProducts ?? []).slice(0, 6).map((p) => ({
    name:    p.name?.length > 17 ? p.name.slice(0, 16) + '…' : (p.name ?? ''),
    Revenue: p.revenue ?? 0,
  }));

  const orderStatusData = (stats?.orderStatusBreakdown ?? []).map(([status, count]) => ({
    key:   status,
    name:  getStatusLabel(t, status),
    count,
  }));
  const statusTotal = orderStatusData.reduce((s, d) => s + d.count, 0) || 1;

  const deliverySegments = [
    { name: 'Completed',   value: stats?.deliveryCompleted  ?? 0, color: G[600] },
    { name: 'In Progress', value: stats?.deliveryInProgress ?? 0, color: G[300] },
    { name: 'Pending',     value: stats?.deliveryPending    ?? 0, color: AMBER },
  ];
  const deliveryTotal = stats?.deliveryTotal ?? 0;
  const activeStores  = stats?.activeStores ?? 0;
  const totalStores   = stats?.totalStores  ?? 0;

  /* ── Insights ───────────────────────────────────────────────── */
  const insights = [];
  if (lowStock > 0)
    insights.push({ type: 'warn',    text: t('dashboard.insightLowStock', { count: lowStock }),   id: 'lowStock',   detail: stats.lowStockList,         linkTo: canAccessProducts ? '/dashboard/products' : null, linkLabel: t('dashboard.viewProducts') });
  if (stats?.revenueGrowth != null && stats.revenueGrowth !== 0)
    insights.push({ type: stats.revenueGrowth > 0 ? 'success' : 'warn', text: t('dashboard.insightRevenue', { sign: stats.revenueGrowth > 0 ? '+' : '', pct: stats.revenueGrowth.toFixed(1) }), id: 'revenue' });
  if (stats?.ordersGrowth  != null && stats.ordersGrowth  !== 0)
    insights.push({ type: stats.ordersGrowth  > 0 ? 'success' : 'warn', text: t('dashboard.insightOrders', { sign: stats.ordersGrowth > 0 ? '+' : '', pct: stats.ordersGrowth.toFixed(1) }),   id: 'orders'  });
  if (delPending > 0)
    insights.push({ type: 'info',    text: t('dashboard.insightDeliveriesPending', { count: delPending }),     id: 'deliveries', detail: stats.pendingDeliveriesList, linkTo: canAccessRequests ? '/dashboard/requests' : null, linkLabel: t('dashboard.viewRequests') });
  if (outOfStock > 0)
    insights.push({ type: 'warn',    text: t('dashboard.insightOutOfStock', { count: outOfStock }),  id: 'outOfStock', detail: stats.outOfStockList,        linkTo: canAccessProducts ? '/dashboard/products' : null, linkLabel: t('dashboard.viewProducts') });

  const revColor = focus === 'revenue' ? G[600] : MUTED;
  const ordColor = focus === 'orders'  ? G[600] : MUTED;

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className={`dash-page${loading ? ' dash-refreshing' : ''}`}>
      {permissionMessage && (
        <div className="dash-permission-banner">{permissionMessage}</div>
      )}

      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">{t('nav.dashboard')}</h1>
          <p className="dash-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} label={t('dashboard.ordersRevenueLabel')} />
      </div>

      {/* ── Top: 2×2 KPIs + revenue area chart ─────────────────── */}
      <div className="dash-top">
        <KpiCard
          label={t('dashboard.totalOrders')}
          value={fmt(stats?.totalOrders ?? 0)}
          growth={stats?.ordersGrowth}
          hint={t('dashboard.vsPrevious')}
          visual={<MiniBars data={recent} dataKey="orders" color={G[600]} />}
        />
        <KpiCard
          label={t('dashboard.avgOrder')}
          value={fmtK(stats?.avgOrderValue ?? 0)}
          unit="TZS"
          growth={aovGrowth}
          hint={t('dashboard.vsPrevious')}
          visual={<MiniBars data={recent} dataKey="aov" color={G[400]} />}
        />
        <KpiCard
          label={t('dashboard.activeStores')}
          value={fmt(activeStores)}
          hint={t('dashboard.ofTotalStores', { total: totalStores })}
          visual={
            <div className="dash-kpi-ring" style={{ '--pct': `${totalStores ? (activeStores / totalStores) * 100 : 0}%` }}>
              <span>{totalStores ? Math.round((activeStores / totalStores) * 100) : 0}%</span>
            </div>
          }
        />
        <KpiCard
          label={t('dashboard.deliveries')}
          value={fmt(deliveryTotal)}
          hint={delPending > 0 ? t('dashboard.pendingCount', { count: delPending }) : null}
          visual={deliveryTotal > 0 && (
            <div className="dash-kpi-stack" title={deliverySegments.map((d) => `${d.name}: ${d.value}`).join(' · ')}>
              {deliverySegments.map((d) => (
                <span key={d.name} style={{ flexGrow: d.value, background: d.color }} />
              ))}
            </div>
          )}
        />

        <div className="dash-card dash-top-chart">
          <div className="dash-card-head">
            <h3 className="dash-card-title dash-card-title-sm">{t('dashboard.revenueThisPeriod')}</h3>
            <Growth value={stats?.revenueGrowth} hint={t('dashboard.vsPrevious')} />
          </div>
          <div className="dash-big-value">
            {fmt(stats?.totalRevenue ?? 0)} <span>TZS</span>
          </div>
          {trend.length > 1 && hasTrend ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trend} margin={{ top: 10, right: 6, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gRevArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={G[500]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={G[500]} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#F2F4F7" />
                <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} minTickGap={24} dy={6} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} tickFormatter={fmtK} width={44} />
                <Tooltip content={<ChartTooltip suffix=" TZS" />} cursor={{ stroke: G[200] }} />
                <Area type="monotone" dataKey="revenue" name={t('dashboard.revenue')}
                  stroke={G[600]} strokeWidth={2.5} fill="url(#gRevArea)"
                  dot={false} activeDot={{ r: 4, fill: G[600], stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="dash-empty dash-empty-chart">{t('dashboard.noTrendData')}</p>
          )}
        </div>
      </div>

      {/* ── Revenue & orders trend (diverging) + summary ───────── */}
      <div className="dash-card dash-trend">
        <div className="dash-trend-main">
          <div className="dash-card-head">
            <h3 className="dash-card-title">{t('dashboard.revenueOrdersTrend')}</h3>
            <div className="dash-toggle" role="group" aria-label={t('dashboard.revenueOrdersTrend')}>
              <button type="button" className={focus === 'revenue' ? 'active' : ''} onClick={() => setFocus('revenue')}>
                {t('dashboard.revenue')}
              </button>
              <button type="button" className={focus === 'orders' ? 'active' : ''} onClick={() => setFocus('orders')}>
                {t('nav.orders')}
              </button>
            </div>
          </div>

          {hasTrend ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={divergingData} barSize={barSize} barGap={-barSize}
                  margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
                  <CartesianGrid vertical={false} stroke="#F2F4F7" strokeDasharray="4 4" />
                  <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} minTickGap={16} dy={6} />
                  <YAxis yAxisId="ord" orientation="left" domain={[-ordMax, ordMax]} ticks={ordTicks}
                    tick={AXIS} axisLine={false} tickLine={false} width={36}
                    tickFormatter={(v) => fmtK(Math.abs(v))} />
                  <YAxis yAxisId="rev" orientation="right" domain={[-revMax, revMax]} ticks={revTicks}
                    tick={AXIS} axisLine={false} tickLine={false} width={48} tickFormatter={fmtK} />
                  <ReferenceLine yAxisId="rev" y={0} stroke="#E4E7EC" />
                  <Tooltip
                    cursor={{ fill: 'rgba(3, 152, 85, 0.05)' }}
                    content={<TrendTooltip revenueLabel={t('dashboard.revenue')} ordersLabel={t('nav.orders')} revColor={revColor} ordColor={ordColor} />}
                  />
                  <Bar yAxisId="rev" dataKey="revenue" fill={revColor} radius={barSize / 2} minPointSize={0} />
                  <Bar yAxisId="ord" dataKey="ordersNeg" fill={ordColor} radius={barSize / 2} minPointSize={0} />
                </BarChart>
              </ResponsiveContainer>
              <div className="dash-legend">
                <span><em className="dash-dot" style={{ background: revColor }} /> {t('dashboard.revenueUp')}</span>
                <span><em className="dash-dot" style={{ background: ordColor }} /> {t('dashboard.ordersDown')}</span>
              </div>
            </>
          ) : (
            <p className="dash-empty dash-empty-chart">{t('dashboard.noTrendData')}</p>
          )}
        </div>

        <aside className="dash-trend-side">
          <SummaryStat
            value={fmtK(stats?.totalRevenue ?? 0)}
            label={`${t('dashboard.totalRevenue')} (TZS)`}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/>
              </svg>
            }
          />
          <SummaryStat
            value={fmt(stats?.totalUnitsSold ?? 0)}
            label={t('dashboard.unitsSold')}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19V5"/><path d="m5 12 7-7 7 7"/>
              </svg>
            }
          />
          <SummaryStat
            value={fmt(stats?.totalProducts ?? 0)}
            label={t('dashboard.productsListed')}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
                <path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>
              </svg>
            }
          />
          {canAccessAnalytics && (
            <button type="button" className="dash-primary-btn" onClick={() => navigate('/dashboard/analytics')}>
              {t('dashboard.viewAnalytics')}
            </button>
          )}
        </aside>
      </div>

      {/* ── Breakdown row ─────────────────────────────────────── */}
      <div className="dash-grid-3">
        <Card title={t('dashboard.topProductsByRevenue')}
          onAction={canAccessAnalytics ? () => navigate('/dashboard/analytics') : null}
          actionLabel={t('common.viewAll')}>
          {topProductsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topProductsData} layout="vertical" barSize={10}
                margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={132}
                  tick={{ fontSize: 12, fill: '#475467' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip suffix=" TZS" />} cursor={{ fill: 'rgba(3, 152, 85, 0.05)' }} />
                <Bar dataKey="Revenue" name={t('dashboard.revenue')} fill={G[600]}
                  background={{ fill: '#F2F4F7', radius: 5 }} radius={5} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="dash-empty">{t('dashboard.noProductSales')}</p>
          )}
        </Card>

        <Card title={t('dashboard.orderStatus')}>
          {orderStatusData.length > 0 ? (
            <div className="dash-status-list">
              {orderStatusData.map((item) => {
                const pct   = Math.round((item.count / statusTotal) * 100);
                const color = STATUS_COLORS[item.key] ?? '#98A2B3';
                return (
                  <div key={item.key} className="dash-status-item">
                    <div className="dash-status-row">
                      <span className="dash-status-name">
                        <span className="dash-legend-dot" style={{ background: color }} />
                        {item.name}
                      </span>
                      <span className="dash-status-count">{item.count} <em>{pct}%</em></span>
                    </div>
                    <div className="dash-status-track">
                      <div className="dash-status-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="dash-empty">{t('dashboard.noOrdersPeriod')}</p>
          )}
        </Card>

        <Card title={t('dashboard.userDistribution')}>
          {userPieData.length > 0 ? (
            <Donut data={userPieData} total={fmt(stats?.totalUsers ?? 0)} totalLabel={t('common.users')} />
          ) : (
            <p className="dash-empty">{t('dashboard.noUserData')}</p>
          )}
        </Card>
      </div>

      {/* ── Operations row (full dashboard only) ──────────────── */}
      {canSeeFullDashboard && (
        <div className="dash-grid-auto">
          <Card title={t('dashboard.deliveryStatus')}>
            {deliveryTotal > 0 ? (
              <Donut data={deliverySegments} total={fmt(deliveryTotal)} totalLabel={t('common.total')}
                height={160} inner={48} outer={70} />
            ) : (
              <p className="dash-empty">{t('dashboard.noDeliveriesYet')}</p>
            )}
          </Card>

          {stats?.topStores?.length > 0 && (
            <Card title={t('dashboard.topStores')}>
              <ul className="dash-rank-list">
                {stats.topStores.map((s, i) => (
                  <li key={s.storeId} className="dash-rank-item">
                    <span className="dash-rank-num">{i + 1}</span>
                    <span className="dash-rank-name">{s.storeName}</span>
                    <span className="dash-rank-val">{fmtK(s.revenue)} <em>TZS</em></span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {stats?.topCategories?.length > 0 && (
            <Card title={t('dashboard.topCategories')}>
              <ul className="dash-rank-list">
                {stats.topCategories.map((c, i) => (
                  <li key={c.name} className="dash-rank-item">
                    <span className="dash-rank-num">{i + 1}</span>
                    <span className="dash-rank-name">{c.name}</span>
                    <span className="dash-rank-val">{fmtK(c.revenue)} <em>TZS</em></span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {stats?.deliveryMethodUsage?.length > 0 && (
            <Card title={t('dashboard.deliveryMethods')}>
              <div className="dash-status-list">
                {stats.deliveryMethodUsage.map(([method, count]) => {
                  const total = stats.deliveryMethodUsage.reduce((s, [, n]) => s + n, 0);
                  const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={method} className="dash-status-item">
                      <div className="dash-status-row">
                        <span className="dash-status-name">{getStatusLabel(t, method)}</span>
                        <span className="dash-status-count">{count} <em>{pct}%</em></span>
                      </div>
                      <div className="dash-status-track">
                        <div className="dash-status-fill" style={{ width: `${pct}%`, background: G[600] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── Insights ──────────────────────────────────────────── */}
      {insights.length > 0 && (
        <Card title={t('dashboard.insights')}>
          <ul className="dash-insights">
            {insights.map((ins) => {
              const tone = ins.type === 'success' ? 'up' : (ins.id === 'revenue' || ins.id === 'orders') ? 'down' : 'neutral';
              const shown = (ins.detail ?? []).slice(0, 4);
              const more  = (ins.detail?.length ?? 0) - shown.length;
              return (
                <li key={ins.id} className="dash-insight">
                  <span className={`dash-insight-icon dash-insight-${tone}`}>{insightIcon(ins.id, tone === 'up')}</span>
                  <div className="dash-insight-body">
                    <div className="dash-insight-text">{ins.text}</div>
                    {shown.length > 0 && (
                      <div className="dash-insight-tags">
                        {shown.map((item) => (
                          <span key={item.$id} className="dash-insight-tag"
                            title={item.storeName}>
                            {ins.id === 'deliveries' ? item.packageName : item.productName}
                            {ins.id === 'lowStock' && <em>{t('dashboard.stockLeft', { count: item.stockCount })}</em>}
                          </span>
                        ))}
                        {more > 0 && <span className="dash-insight-tag dash-insight-more">+{more}</span>}
                      </div>
                    )}
                  </div>
                  {ins.linkTo && (
                    <button type="button" className="dash-insight-link" onClick={() => navigate(ins.linkTo)}>
                      {ins.linkLabel}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
